import { InputAdornment, MenuItem, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import EmployeePageButtons from "../employeePageButtons/employeePageButtons";
import "./employeeDigitalWallet.css";
import SearchIcon from "@mui/icons-material/Search";
import CardChipIcon from "@/assets/images/CardChipIcon.svg";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CacheProvider } from "@emotion/react";
import authStore from "@/stores/authStore";
import rtlPlugin from "stylis-plugin-rtl";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import BaseInput from "@/components/common/baseInput";
import BaseDateInput from "@/components/common/baseDateInput";
import BaseButton from "@/components/common/baseButton";
import {
    EmployeeDigitalWalletFilterRequest,
    EmployeeDigitalWalletInfo,
    EmployeeDigitalWalletResponse,
    TransactionRowResult,
} from "@/models/digital.wallet";
import { useCallback, useEffect, useState } from "react";
import popupService from "@/services/popupService";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/services/api";
import { checkPermissionsWithAlert, formatAmount, formatAmountWithoutDecimals, formatDateTime } from "@/util";
import { TransactionType } from "@/models/enums";
import dayjs from "dayjs";
import { IdText, PermissionName } from "@/models/apiModels";
import momnt from "moment";
import Swal from "sweetalert2";
import filtersStore from "@/stores/filtersStore";
import TotalCountComponent from "@/components/common/resultCountComponent";
import ClearIcon from "@/assets/icons/clear-icon.svg";

const cacheLtr = createCache({
    key: "muiltr",
});

const cacheRtl = createCache({
    key: "muirtl",
    stylisPlugins: [prefixer, rtlPlugin],
});

const EmployeeDigitalWallet = () => {
    const { id } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState<TransactionRowResult[]>([]);
    const [hasNext, setHasNext] = useState(true);
    const [searchCounter, setSearchCounter] = useState(0);
    const [total, setTotal] = useState(0);
    const [totalFilter, setTotalFilter] = useState(0);
    const [employeeDigitalWalletInfo, setEmployeeDigitalWalletInfo] = useState<EmployeeDigitalWalletInfo>({
        walletId: 0,
        walletBalance: 0,
        walletStatus: "",
        expiredDate: "",
        currencyId: 0,
    });
    const [employeeDigitalWalletFilter, setEmployeeDigitalWalletFilter] = useState<EmployeeDigitalWalletFilterRequest>({
        employeeId: Number(id),
        keywords: "",
        amount: null,
        fromDate: null,
        tillDate: null,
        transactionType: null,
        orderBy: "",
        asc: false,
        pageIndex: 0,
    });

    const transactionTypeList: IdText[] = (
        Object.values(TransactionType).filter((val) => isNaN(Number(val))) as (keyof typeof TransactionType)[]
    ).map((key) => new IdText(key, t(`transaction.${key}`)));

    function handleClearFilters() {
        setTransactions([]);
        filtersStore.setEmployeeWalletFilterData(null);
        setEmployeeDigitalWalletFilter((prevData) => ({
            ...prevData,
            keywords: "",
            amount: null,
            fromDate: null,
            tillDate: null,
            orderBy: "",
            asc: false,
            transactionType: null,
            pageIndex: 0,
        }));
        setSearchCounter(searchCounter + 1);
    }

    function handleOnChangeFromDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setEmployeeDigitalWalletFilter((prevData) => ({
            ...prevData,
            fromDate: date.isValid() ? formattedDate : null,
        }));
    }

    function handleOnChangeTillDate(e: any) {
        const date = dayjs(e?.toString());
        const formattedDate = date.isValid() ? date.format("YYYY-MM-DD") : "";
        setEmployeeDigitalWalletFilter((prevData) => ({
            ...prevData,
            tillDate: date.isValid() ? formattedDate : null,
        }));
    }

    async function onSearchClick() {
        setTransactions([]);
        popupService.showLoading();
        filtersStore.setEmployeeWalletFilterData(null);
        setEmployeeDigitalWalletFilter((prevData) => ({
            ...prevData,
            pageIndex: 0,
        }));
        setSearchCounter(searchCounter + 1);
    }

    async function getEmployeeDigitalWallet(): Promise<EmployeeDigitalWalletResponse> {
        const filterStore = filtersStore.employeesWalletFilterData;
        if (filterStore != null && filterStore.employeeId === Number(id)) {
            if (filterStore.pageIndex !== 0) {
                filterStore.pageIndex = 0;
            }
            setEmployeeDigitalWalletFilter(filterStore ?? new EmployeeDigitalWalletFilterRequest());
            const responsePromise = api.getEmployeeDigitalWallet(
                filterStore ?? new EmployeeDigitalWalletFilterRequest()
            );
            return (await responsePromise).data;
        }

        filtersStore.setEmployeeWalletFilterData(employeeDigitalWalletFilter);
        const responsePromise = api.getEmployeeDigitalWallet(employeeDigitalWalletFilter);
        return (await responsePromise).data;
    }

    const handleScroll = useCallback(() => {
        const walletContainer = document.getElementById("employee-wallet-list-grid");
        const doScroll =
            walletContainer &&
            walletContainer.scrollTop + walletContainer.clientHeight >= walletContainer.scrollHeight &&
            walletContainer.scrollTop > 0;

        if (!doScroll || !hasNext) {
            return;
        }
        filtersStore.setEmployeeWalletFilterData(null);
        setEmployeeDigitalWalletFilter((prevData) => ({
            ...prevData,
            pageIndex: employeeDigitalWalletFilter.pageIndex + 1,
        }));
    }, [employeeDigitalWalletFilter.pageIndex, hasNext]);

    let startFetchingBusy = false;
    async function startFetching() {
        if (!checkPermissionsWithAlert(PermissionName.EmployeeDigitalWallet)) {
            return;
        }
        if (startFetchingBusy) {
            return;
        }
        try {
            startFetchingBusy = true;
            popupService.showLoading();
            if (employeeDigitalWalletFilter.fromDate && employeeDigitalWalletFilter.tillDate) {
                if (momnt(employeeDigitalWalletFilter.fromDate).isAfter(employeeDigitalWalletFilter.tillDate)) {
                    Swal.fire({
                        icon: "error",
                        text: t("employee.alert.incorrectFilterDates") as string,
                    });
                    return;
                }
            }
            const data = await getEmployeeDigitalWallet();
            setEmployeeDigitalWalletInfo(data.employeeDigitalWalletInfo);
            setHasNext(data.hasNext);
            if (employeeDigitalWalletFilter.pageIndex === 0) {
                setTransactions(data.transactions);
                setTotal(data.total);
                setTotalFilter(data.totalFilter);
            } else {
                setTransactions((oldArray) => [...oldArray, ...data.transactions]);
            }
            popupService.close();
        } catch (err) {
            popupService.showApiError(err);
            return;
        } finally {
            startFetchingBusy = false;
        }
    }

    async function sortBy(sortBy: string) {
        if (employeeDigitalWalletFilter.orderBy !== sortBy) {
            setEmployeeDigitalWalletFilter((prevData) => ({
                ...prevData,
                asc: true,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        } else {
            setEmployeeDigitalWalletFilter((prevData) => ({
                ...prevData,
                asc: !employeeDigitalWalletFilter.asc,
                orderBy: sortBy,
                pageIndex: 0,
            }));
        }
        setTransactions([]);
        filtersStore.setEmployeeWalletFilterData(null);
    }

    function getArrowClass(sort: string) {
        if (sort === employeeDigitalWalletFilter.orderBy) {
            if (employeeDigitalWalletFilter.asc) {
                return "top-sort-arrow";
            } else {
                return "bottom-sort-arrow";
            }
        }
        return "icon-SortArrowIcon sort-arrow-icon";
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            startFetching();
        }
        return () => {
            ignore = true;
        };
    }, [
        employeeDigitalWalletFilter.pageIndex,
        employeeDigitalWalletFilter.orderBy,
        employeeDigitalWalletFilter.asc,
        searchCounter,
    ]);

    useEffect(() => {
        const walletContainer = document.getElementById("employee-wallet-list-grid");
        if (!walletContainer) {
            return;
        }
        walletContainer.addEventListener("scroll", handleScroll);
        return () => {
            walletContainer.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    function handleKeyPressSearch(event: any) {
        if (event.key === "Enter") {
            onSearchClick();
        }
    }

    useEffect(() => {
        document.addEventListener("keypress", handleKeyPressSearch);
        return () => {
            document.removeEventListener("keypress", handleKeyPressSearch);
        };
    });

    return (
        <CacheProvider value={authStore.isRtl ? cacheRtl : cacheLtr}>
            {checkPermissionsWithAlert(PermissionName.EmployeeDigitalWallet) ? (
                <div className="emp-digital-wallet-container">
                    <div className="emp-digital-wallet-header">
                        <div>
                            <EmployeePageButtons />
                            <div className="emp-digital-wallet-filter">
                                <div className="font-style emp-digital-wallet-span">{t("employee.FilterHeader")}</div>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                                    <div className="emp-digital-wallet-filter-row">
                                        <div>
                                            <BaseInput
                                                id="keyword-input"
                                                className="search-input"
                                                placeholder={`${t("general.searchByAmount")}`}
                                                type="search"
                                                size="small"
                                                value={employeeDigitalWalletFilter.amount ?? ""}
                                                onChange={(event) =>
                                                    setEmployeeDigitalWalletFilter((prevData) => ({
                                                        ...prevData,
                                                        amount: event.target.value ? Number(event.target.value) : null,
                                                    }))
                                                }
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    width: "200px",
                                                }}
                                            />
                                        </div>
                                        <div>
                                            <BaseInput
                                                select
                                                id="transaction-type-input"
                                                label={t("employee.Wallet.TransType")}
                                                type="search"
                                                size="small"
                                                onKeyDown={handleKeyPressSearch}
                                                value={employeeDigitalWalletFilter.transactionType ?? ""}
                                                onChange={(event: any) =>
                                                    setEmployeeDigitalWalletFilter((prevData) => ({
                                                        ...prevData,
                                                        transactionType: event.target.value,
                                                    }))
                                                }
                                                sx={{
                                                    width: "165px",
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <IconButton
                                                            className="select-clear-icon"
                                                            sx={{
                                                                visibility: employeeDigitalWalletFilter.transactionType
                                                                    ? "visible"
                                                                    : "hidden",
                                                            }}
                                                            onClick={() =>
                                                                setEmployeeDigitalWalletFilter((prevData) => ({
                                                                    ...prevData,
                                                                    transactionType: null,
                                                                }))
                                                            }
                                                        >
                                                            <img src={ClearIcon} alt="Clear icon" />
                                                        </IconButton>
                                                    ),
                                                }}
                                            >
                                                {transactionTypeList.map((option) => (
                                                    <MenuItem key={option.id} value={option.id}>
                                                        {option.text}
                                                    </MenuItem>
                                                ))}
                                            </BaseInput>
                                        </div>
                                        <div style={{ width: 165 }}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DesktopDatePicker
                                                    className="date-picker-input"
                                                    label={t("display.Date.FromDate")}
                                                    inputFormat="DD/MM/YYYY"
                                                    value={employeeDigitalWalletFilter.fromDate}
                                                    onChange={handleOnChangeFromDate}
                                                    maxDate={employeeDigitalWalletFilter.tillDate}
                                                    renderInput={(params) => <BaseDateInput {...params} />}
                                                />
                                            </LocalizationProvider>
                                        </div>
                                        <div style={{ width: 165 }}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DesktopDatePicker
                                                    className="date-picker-input"
                                                    label={t("display.Date.ToDate")}
                                                    inputFormat="DD/MM/YYYY"
                                                    value={employeeDigitalWalletFilter.tillDate}
                                                    onChange={handleOnChangeTillDate}
                                                    minDate={employeeDigitalWalletFilter.fromDate}
                                                    renderInput={(params) => <BaseDateInput {...params} />}
                                                />
                                            </LocalizationProvider>
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            paddingInlineEnd: 15,
                                            gap: 15,
                                            marginInlineStart: 10,
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                                minWidth: "70px",
                                            }}
                                            onClick={handleClearFilters}
                                        >
                                            <span
                                                className="link-element clear-all-btn"
                                                style={{
                                                    minWidth: "70px",
                                                }}
                                            >
                                                {t("display.ClearAll")}
                                            </span>
                                        </div>
                                        <BaseButton
                                            style={{ padding: "0px 25px" }}
                                            className="basic-button"
                                            type="button"
                                            onClick={() => onSearchClick()}
                                        >
                                            {t("general.filter")}
                                        </BaseButton>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="emp-digital-wallet-card">
                            <div className="card-status-balance">
                                <div style={{ display: "grid", gap: 10 }}>
                                    <div className="font-style-white">
                                        {t(`enums.DigitalWalletStatus.${employeeDigitalWalletInfo.walletStatus}`)}
                                    </div>
                                    <img src={CardChipIcon} alt="CardChipIcon"></img>
                                </div>
                                <div>
                                    <div
                                        style={{ display: "flex", justifyContent: "flex-end" }}
                                        className="font-style-white"
                                    >
                                        {t("employee.Wallet.CurrentBalance")}
                                    </div>
                                    <div
                                        className="font-style-bold-24-white "
                                        style={{ display: "flex", justifyContent: "flex-end" }}
                                    >
                                        {formatAmountWithoutDecimals(
                                            employeeDigitalWalletInfo.walletBalance,
                                            employeeDigitalWalletInfo.currencyId
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="card-expiration-date">
                                <div className="font-style-white-12">{t("employee.Wallet.ExpDate")}</div>
                                <div className="font-style-bold-white">{employeeDigitalWalletInfo.expiredDate}</div>
                            </div>
                        </div>
                    </div>
                    <TotalCountComponent
                        title={t("digitalWallet.QuantityOfWallets") as string}
                        allCount={total}
                        currentCount={totalFilter}
                    />
                    <div className="emp-digital-wallet-content">
                        <div id="employee-wallet-list-grid" className="employee-wallet-list-grid">
                            <div className="wallets-fake-thead"></div>
                            <table>
                                <thead>
                                    <tr>
                                        <td style={{ width: "15%" }}>
                                            <div style={{ width: "80%" }} className="table-number-format-name">
                                                {t("employee.Wallet.TransNumber")}
                                            </div>
                                        </td>
                                        <td onClick={() => sortBy("TransType")} style={{ width: "15%" }}>
                                            <span>
                                                {t("employee.Wallet.TransType")}
                                                <i className={`icon-f ${getArrowClass("TransType")} fs-12`} />
                                            </span>
                                        </td>
                                        <td onClick={() => sortBy("CreationDate")} style={{ width: "12%" }}>
                                            <div className="table-number-format-name">
                                                <span>
                                                    {t("interfaces.creationDate")}
                                                    <i className={`icon-f ${getArrowClass("CreationDate")} fs-12`} />
                                                </span>
                                            </div>
                                        </td>
                                        <td onClick={() => sortBy("User")} style={{ width: "13%" }}>
                                            <span>
                                                {t("interfaces.user")}
                                                <i className={`icon-f ${getArrowClass("User")} fs-12`} />
                                            </span>
                                        </td>
                                        <td style={{ width: "12%" }}>
                                            <div className="table-number-format-name">
                                                {t("employee.Wallet.Amount")}
                                            </div>
                                        </td>
                                        <td style={{ width: "15%" }}>
                                            <div className="table-number-format-name">
                                                {t("employee.Wallet.LinkedOrder")}
                                            </div>
                                        </td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((transaction, index) => (
                                        <tr key={index}>
                                            <td>
                                                <div style={{ width: "78%" }} className="table-number-format-value">
                                                    {transaction.id}
                                                </div>
                                            </td>
                                            <td>{t(`transaction.${transaction.transactionType}`)}</td>
                                            <td>
                                                <div className="table-number-format-value">
                                                    {formatDateTime(transaction.transactionDate)}
                                                </div>
                                            </td>
                                            <td>{transaction.username}</td>
                                            <td className="amount-data">
                                                <div className="table-number-format-value">
                                                    <div
                                                        className={`transaction-amount ${transaction.transactionType}`}
                                                    >
                                                        {formatAmount(
                                                            transaction.transactionAmount,
                                                            transaction.currencyId
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                {transaction.connectedOrderId != null ? (
                                                    <div
                                                        className="link-element table-number-format-value"
                                                        onClick={() =>
                                                            navigate(
                                                                `/employees/${id}/wallet/${transaction.connectedOrderId}`,
                                                                { state: { docketNumber: transaction.docket } }
                                                            )
                                                        }
                                                    >
                                                        {transaction.docket}
                                                    </div>
                                                ) : (
                                                    ""
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                ""
            )}
        </CacheProvider>
    );
};

export default EmployeeDigitalWallet;
